import { useState, useRef, useEffect } from "react";
import { Link } from "wouter";
import { Search, Mic, MicOff, Send, Loader2, CalendarCheck, Check } from "lucide-react";
import { useSpeechInput } from "@/hooks/useSpeechInput";
import { motion, AnimatePresence } from "framer-motion";
import { useSendChatMessage } from "@workspace/api-client-react";
import agentsToGoLogo from "@assets/AGENTS-02_1_1778254256444.webp";
import { ChatMessage } from "@/components/ChatMessage";
import { CheckoutScreen } from "@/components/CheckoutScreen";
import { FAQModal } from "@/components/FAQModal";
import { AgentsModal } from "@/components/AgentsModal";
import { CatalogueModal, type CatalogueAgent } from "@/components/CatalogueModal";
import { Seo, serviceSchema } from "@/lib/seo";
import { StoryModal } from "@/components/StoryModal";
import { PrivacyModal } from "@/components/PrivacyModal";
import { TermsModal } from "@/components/TermsModal";
import { HowItWorks } from "@/components/HowItWorks";
import { LogoCarousel } from "@/components/LogoCarousel";
import { AgentMatchPanel } from "@/components/AgentMatchPanel";
import { extractAgentMatches, type ParsedAgentMatch } from "@/lib/agentCatalogue";

type Message = {
  id: string;
  role: "user" | "agent";
  content: string;
};

export default function Home() {
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<Message[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [inputFocused, setInputFocused] = useState(false);
  const [showBooking, setShowBooking] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);
  const [showAgents, setShowAgents] = useState(false);
  const [showCatalogue, setShowCatalogue] = useState(false);
  const [showStory, setShowStory] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [ctaUnlocked, setCtaUnlocked] = useState(false);
  const [chatViewOpen, setChatViewOpen] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutAgents, setCheckoutAgents] = useState<Array<{ name: string; price: number }>>([]);
  const [matchedAgents, setMatchedAgents] = useState<ParsedAgentMatch[]>([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const [mobilePanelExpanded, setMobilePanelExpanded] = useState(false);
  const [selectedAgentIndexes, setSelectedAgentIndexes] = useState<Set<number>>(new Set());
  const [catalogueAgents, setCatalogueAgents] = useState<CatalogueAgent[]>([]);
  const [catalogueLoaded, setCatalogueLoaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);

  const { status: micStatus, toggle: toggleMic } = useSpeechInput({
    onTranscript: (text) => setInput(text),
  });

  const placeholders = [
    "I need a Discord support bot",
    "I want to automate my lead follow-ups",
    "I'm looking for an image content validator",
    "I need to process invoices automatically",
    "I want a sentiment tracker for my community",
    "I'm looking for a meeting notes extractor",
    "I need a PII masker for file uploads",
    "I want to enrich new leads automatically",
    "I'm looking for a product listing validator",
    "I need an agent to handle customer onboarding",
  ];
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayedPlaceholder, setDisplayedPlaceholder] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
  useEffect(() => {
    fetch(`${BASE}/api/catalogue`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`Catalogue fetch failed (${r.status})`);
        return r.json() as Promise<Array<{ id: number; name: string; role: string; description: string; price: number; imageUrl?: string | null }>>;
      })
      .then((data) => {
        setCatalogueAgents(data.map((a) => ({
          id: String(a.id),
          name: a.name,
          tagline: a.role,
          description: a.description,
          price: a.price,
          imageUrl: a.imageUrl ?? null,
        })));
      })
      .catch(() => {})
      .finally(() => setCatalogueLoaded(true));
  }, []);

  useEffect(() => {
    if (messages.length > 0) return;
    const current = placeholders[placeholderIndex];
    let timeout: ReturnType<typeof setTimeout>;

    if (!isDeleting && charIndex < current.length) {
      timeout = setTimeout(() => setCharIndex((c) => c + 1), 38);
    } else if (!isDeleting && charIndex === current.length) {
      timeout = setTimeout(() => setIsDeleting(true), 2200);
    } else if (isDeleting && charIndex > 0) {
      timeout = setTimeout(() => setCharIndex((c) => c - 1), 18);
    } else if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setPlaceholderIndex((i) => (i + 1) % placeholders.length);
    }

    setDisplayedPlaceholder(current.slice(0, charIndex));
    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, placeholderIndex, messages.length]);

  const { mutate, isPending } = useSendChatMessage({
    mutation: {
      onSuccess: (data) => {
        setSessionId(data.sessionId);
        setMessages((prev) => [
          ...prev,
          { id: Date.now().toString(), role: "agent", content: data.reply },
        ]);
        if (data.showCTA) {
          setCtaUnlocked(true);
        }
        const { matches } = extractAgentMatches(data.reply);
        if (matches.length > 0) {
          setMatchedAgents(matches);
          setSelectedAgentIndexes(new Set(matches.map((_, i) => i)));
          setPanelOpen(true);
          setMobilePanelExpanded(false);
        }
      },
      onError: () => {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now().toString(),
            role: "agent",
            content: "Sorry, I'm having trouble connecting right now. Please try again later.",
          },
        ]);
      },
    },
  });

  const sendMessage = (textToSubmit: string, currentMessages: Message[]) => {
    const historyToSend = currentMessages
      .map((m) => ({
        role: (m.role === "agent" ? "assistant" : m.role) as "user" | "assistant",
        content: m.content,
      }));

    mutate({
      data: {
        message: textToSubmit,
        sessionId,
        history: historyToSend,
        ctaAlreadyShown: ctaUnlocked,
      },
    });
  };

  const handleSubmit = (e?: React.FormEvent, presetInput?: string) => {
    e?.preventDefault();
    const textToSubmit = presetInput || input;
    if (!textToSubmit.trim() || isPending) return;

    setChatViewOpen(true);
    const newMsg: Message = { id: Date.now().toString(), role: "user", content: textToSubmit.trim() };
    setMessages((prev) => [...prev, newMsg]);
    setInput("");
    sendMessage(textToSubmit.trim(), messages);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleBuildAgent = (agent: CatalogueAgent) => {
    setSessionId(undefined);
    setCtaUnlocked(false);
    setShowCheckout(false);
    setCheckoutAgents([]);
    setShowBooking(false);
    setMatchedAgents([]);
    setPanelOpen(false);

    const contextMessage = `I'm interested in building ${agent.name} — ${agent.tagline}. ${agent.description}`;
    const displayMessage = `I want to build the ${agent.name} agent (${agent.tagline}).`;
    setChatViewOpen(true);
    const newMsg: Message = { id: Date.now().toString(), role: "user", content: displayMessage };
    setMessages([newMsg]);
    mutate({
      data: {
        message: contextMessage,
        sessionId: undefined,
        history: [],
        ctaAlreadyShown: false,
      },
    });
  };

  const handleToggleAgentIndex = (i: number) => {
    setSelectedAgentIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };

  const handleProceed = (agents: Array<{ name: string; price: number }>) => {
    setCheckoutAgents(agents);
    setShowCheckout(true);
    setPanelOpen(false);
  };

  // Auto-scroll to bottom as messages arrive
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isPending]);


  const hasMessages = messages.length > 0 || chatViewOpen;
  const userMessageCount = messages.filter((m) => m.role === "user").length;

  // Focus input after every agent reply (multiple attempts to defeat iframe focus race conditions)
  const messagesLengthRef = useRef(0);
  useEffect(() => {
    const lastMsg = messages[messages.length - 1];
    if (messages.length > messagesLengthRef.current && lastMsg?.role === "agent") {
      const tryFocus = () => inputRef.current?.focus();
      requestAnimationFrame(() => requestAnimationFrame(tryFocus));
      setTimeout(tryFocus, 50);
      setTimeout(tryFocus, 200);
      setTimeout(tryFocus, 500);
    }
    messagesLengthRef.current = messages.length;
  }, [messages]);

  // Fallback: unlock CTA after 5 user messages if the model hasn't fired it yet
  useEffect(() => {
    if (userMessageCount >= 5 && !ctaUnlocked) {
      setCtaUnlocked(true);
    }
  }, [userMessageCount]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("browse") === "1") setShowCatalogue(true);
    if (params.get("book") === "1") setShowBooking(true);
  }, []);


  const inputBar = (
    <form
      onSubmit={(e) => handleSubmit(e)}
      className="relative flex items-center w-full rounded-2xl transition-all duration-200 h-14 sm:h-16 px-4 sm:px-6"
      style={{
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        background: "rgba(10,8,22,0.65)",
        border: "1px solid rgba(0,229,255,0.25)",
        boxShadow: "0 0 0 1px rgba(192,38,211,0.10), 0 0 28px rgba(0,229,255,0.10), inset 0 1px 0 rgba(255,255,255,0.04)",
      }}
    >
      <span
        className="text-cyan-400 font-bold text-lg shrink-0 select-none"
        style={{ fontFamily: "'JetBrains Mono', monospace" }}
        aria-hidden
      >
        &gt;
      </span>
      <Search className="text-cyan-400/80 w-5 h-5 shrink-0 ml-2" />
      <div className="relative flex-grow min-w-[120px] h-full flex items-center px-4">
        <input
          ref={inputRef}
          type="text"
          aria-label="Describe what you need an AI agent for"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setInputFocused(true)}
          onBlur={() => setInputFocused(false)}
          className="absolute inset-0 bg-transparent border-none outline-none text-[15px] sm:text-base text-white h-full w-full pl-4 disabled:cursor-not-allowed caret-cyan-400"
          disabled={isPending}
        />
        {!input && !hasMessages && (
          <span
            className="pointer-events-none text-[15px] sm:text-base text-slate-400 whitespace-nowrap overflow-hidden select-none"
            aria-hidden
          >
            {displayedPlaceholder}
            {!inputFocused && (
              <span className="inline-block w-[2px] h-[1em] bg-cyan-400 ml-[1px] align-middle animate-pulse" />
            )}
          </span>
        )}
        {!input && hasMessages && (
          <span className="pointer-events-none text-[15px] sm:text-base text-slate-400 select-none" aria-hidden>
            Ask a follow-up…
          </span>
        )}
      </div>
      {input.trim() ? (
        <button
          type="submit"
          disabled={isPending}
          className="p-2 rounded-full text-cyan-400 hover:bg-cyan-400/10 transition-colors disabled:opacity-50 shrink-0"
        >
          {isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        </button>
      ) : micStatus === "denied" ? (
        <button
          type="button"
          title="Microphone access denied — please allow it in browser settings"
          className="p-2 rounded-full text-red-400 shrink-0 cursor-not-allowed"
        >
          <MicOff className="w-5 h-5" />
        </button>
      ) : micStatus === "unsupported" ? null : (
        <button
          type="button"
          title={micStatus === "listening" ? "Stop listening" : "Speak your message"}
          onClick={toggleMic}
          className={`relative p-2 rounded-full transition-colors shrink-0 ${
            micStatus === "listening"
              ? "text-red-400 bg-red-500/10 hover:bg-red-500/20"
              : "text-cyan-400 hover:bg-cyan-400/10"
          }`}
        >
          {micStatus === "listening" && (
            <span className="absolute inset-0 rounded-full bg-red-400 opacity-25 animate-ping" />
          )}
          <Mic className="w-5 h-5 relative z-10" />
        </button>
      )}
    </form>
  );

  return (
    <div
      className="scifi-wave-bg h-screen flex flex-col text-foreground font-sans overflow-hidden"
    >
      <Seo
        title="AgentsToGo | Expert-Built, Fully Managed AI Agents for Your Business"
        description="AgentsToGo builds, integrates, and deploys custom AI agents for your business — support, sales, finance, and operations workflows. Done-for-you implementation, typically live in 5–10 business days, with a 100% money-back guarantee."
        path="/"
        jsonLd={
          catalogueAgents.length > 0
            ? serviceSchema(
                catalogueAgents.map((a) => ({
                  name: a.name,
                  role: a.tagline,
                  description: a.description,
                })),
              )
            : undefined
        }
      />
      {/* Top Navigation — dark glass pill */}
      <div className="w-full z-50 shrink-0 px-4 sm:px-6 pt-4">
      <nav
        className="flex justify-between items-center px-5 sm:px-6 py-2.5 w-full max-w-6xl mx-auto rounded-full"
        style={{
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          background: "rgba(8,6,18,0.65)",
          border: "1px solid rgba(192,38,211,0.22)",
          boxShadow: "0 0 0 1px rgba(0,229,255,0.05), 0 8px 32px rgba(0,0,0,0.35)",
        }}
      >
        <Link
          href="/"
          className="flex items-center gap-2.5 hover:opacity-75 transition-opacity"
          onClick={() => {
            setMessages([]);
            setInput("");
            setSessionId(undefined);
            setShowBooking(false);
            setCtaUnlocked(false);
            setChatViewOpen(false);
            setShowCheckout(false);
            setCheckoutAgents([]);
            setMatchedAgents([]);
            setPanelOpen(false);
          }}
        >
          <div className="flex flex-row items-center gap-2">
            <img
              src={agentsToGoLogo}
              alt="AgentsToGo"
              className="h-6 sm:h-7 w-auto object-contain"
            />
            <p
              className="text-[0.75rem] font-semibold tracking-tight leading-none"
              style={{ fontFamily: "'Hanken Grotesk', sans-serif", color: "#cbd5e1" }}
            >
              The w<span style={{ color: "#22d3ee", fontWeight: 700 }}>AI</span> to go
            </p>
          </div>
        </Link>
        <div className="flex items-center gap-6 text-sm font-medium">
          <Link
            href="/how-it-works"
            className="hidden sm:block text-slate-300 hover:text-cyan-300 transition-colors"
          >
            How it Works
          </Link>
          <Link
            href="/about"
            className="hidden text-slate-300 hover:text-cyan-300 transition-colors"
          >
            About
          </Link>
          <Link
            href="/faqs"
            className="hidden sm:block text-slate-300 hover:text-cyan-300 transition-colors"
          >
            FAQs
          </Link>
        </div>
      </nav>
      </div>

      {/* ── EMPTY STATE ── */}
      <AnimatePresence>
        {!hasMessages && (
          <motion.main
            key="empty"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex-1 overflow-y-auto"
          >
            {/* Hero — fills the visible area */}
            <div className="relative flex flex-col items-center px-4 sm:px-6 pt-36 pb-5 overflow-hidden">
              {/* Animated wave overlay scanlines */}
              <div className="scifi-wave-overlay absolute inset-0 pointer-events-none opacity-40" />

              {/* Big display headline */}
              <div className="relative z-10 mb-5 text-center">
                <div
                  className="text-4xl sm:text-6xl lg:text-7xl leading-tight tracking-tight text-white mb-3"
                  style={{ fontFamily: "'Montserrat', sans-serif", fontOpticalSizing: "auto", fontWeight: 300, fontStyle: "normal" }}
                >
                  Expert-built AI Agents
                </div>
                <div
                  className="text-2xl sm:text-4xl lg:text-5xl font-extrabold leading-tight tracking-tight"
                  style={{
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    color: "#22d3ee",
                    textShadow: "0 0 40px rgba(0,229,255,0.6), 0 0 80px rgba(0,229,255,0.3)",
                  }}
                >
                  The w<span style={{ color: "#ffffff" }}>AI</span> to go
                </div>
              </div>

              {/* Terminal status label */}
              <div className="relative z-10 mb-7 flex items-center gap-2">
                <span className="terminal-label text-fuchsia-400">SYSTEM_READY</span>
                <span className="terminal-label text-slate-500">//</span>
                <span className="terminal-label text-fuchsia-400">AWAITING_INPUT</span>
              </div>

              {/* Search bar */}
              <div className="relative z-10 w-full max-w-4xl">
                <div className="flex flex-col sm:flex-row items-center gap-3">
                  <div className="flex-1 h-14 sm:h-16">{inputBar}</div>
                  <button
                    type="button"
                    onClick={() => handleSubmit()}
                    disabled={isPending}
                    className="shrink-0 bg-cyan-400 hover:bg-cyan-300 active:bg-cyan-500 disabled:opacity-50 text-slate-950 font-semibold px-7 rounded-2xl transition-colors h-14 sm:h-16 text-[15px] cursor-pointer"
                    style={{ boxShadow: "0 0 24px rgba(0,229,255,0.45), 0 0 48px rgba(0,229,255,0.18)" }}
                  >
                    Find My Agent
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCatalogue(true)}
                    className="shrink-0 border-2 border-cyan-400/70 text-cyan-300 hover:bg-cyan-400/10 active:bg-cyan-400/20 font-semibold px-7 rounded-2xl transition-colors h-14 sm:h-16 text-[15px] cursor-pointer"
                  >
                    Browse Agents
                  </button>
                </div>
                {/* Trust line — sci-fi terminal style */}
                <p
                  className="relative z-10 mt-3 text-center text-xs text-slate-400"
                  style={{ fontFamily: "'Spline Sans', sans-serif" }}
                >
                  <span className="text-cyan-400 font-semibold">✓</span> 100% Satisfaction Guarantee on all custom builds.
                </p>
              </div>

              {/* Logo carousel + trust label */}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.4 }}
                className="relative z-10 w-full max-w-5xl mt-32"
              >
                <p className="text-center terminal-label text-slate-500 mb-1 px-4">
                  ENTERPRISE-GRADE TOOLS IMPLEMENTED ACROSS YOUR ENTIRE STACK
                </p>
                <LogoCarousel />
              </motion.div>

            </div>

            {/* Headline + sub-headline — between carousel and How it Works */}
            <div className="flex flex-col items-center text-center px-4 sm:px-6 pt-32 pb-20 max-w-3xl mx-auto">
              <h2
                className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight cyan-glow"
                style={{
                  fontFamily: "'Hanken Grotesk', sans-serif",
                  background: "linear-gradient(90deg, #ffffff 0%, #67e8f9 50%, #22d3ee 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                Your one-stop shop for expert-built AI agents.
              </h2>
              <p
                className="mt-5 text-lg sm:text-xl font-normal leading-relaxed text-slate-300/90 max-w-2xl"
                style={{ fontFamily: "'Spline Sans', sans-serif" }}
              >
                Find the perfect AI agent for your business in our curated catalog. Get a fixed-price and a custom-implemented solution!
              </p>
            </div>

            {/* How it Works section */}
            <HowItWorks />
          </motion.main>
        )}
      </AnimatePresence>

      {/* ── CHAT STATE ── */}
      <AnimatePresence>
        {hasMessages && (
          <motion.div
            key="chat"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex-1 flex flex-col overflow-hidden relative"
          >
            {/* Milestone progress bar */}
            {(() => {
              const currentStep = showCheckout ? 3 : ctaUnlocked ? 2 : 1;
              const steps = ["Discovery", "Pick your Agent", "Checkout", "Kickoff"];
              return (
                <div className="shrink-0 border-b border-cyan-400/15 bg-black/60 backdrop-blur-sm px-4 py-3">
                  <div className="max-w-3xl mx-auto flex items-center gap-3">
                    {/* Back button — always visible in chat view */}
                    {(
                      <button
                        onClick={() => {
                          setMessages([]);
                          setInput("");
                          setSessionId(undefined);
                          setShowBooking(false);
                          setCtaUnlocked(false);
                          setChatViewOpen(false);
                          setShowCheckout(false);
                          setCheckoutAgents([]);
                          setMatchedAgents([]);
                          setPanelOpen(false);
                        }}
                        className="shrink-0 flex items-center gap-1 text-[11px] font-semibold text-slate-400 hover:text-cyan-300 transition-colors mb-4"
                        title="Back to homepage"
                      >
                        <svg viewBox="0 0 16 16" className="w-3 h-3 fill-current" aria-hidden><path d="M10.5 3L5.5 8l5 5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        Back
                      </button>
                    )}
                    <div className="flex items-center flex-1">
                      {steps.map((label, i) => {
                        const stepNum = i + 1;
                        const isComplete = currentStep > stepNum;
                        const isActive = currentStep === stepNum;
                        return (
                          <div key={label} className="flex items-center flex-1 last:flex-none">
                            <div className="flex flex-col items-center gap-1 shrink-0">
                              <div
                                className={[
                                  "w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300",
                                  isComplete
                                    ? "bg-cyan-400"
                                    : isActive
                                    ? "border-2 border-cyan-400 bg-black"
                                    : "border-2 border-slate-700 bg-black",
                                ].join(" ")}
                              >
                                {isComplete ? (
                                  <Check size={12} className="text-slate-950" strokeWidth={2.5} />
                                ) : isActive ? (
                                  <div className="w-2 h-2 rounded-full bg-cyan-400" />
                                ) : null}
                              </div>
                              <span
                                className={[
                                  "text-[10px] font-semibold whitespace-nowrap leading-tight",
                                  isComplete || isActive ? "text-cyan-300" : "text-slate-500",
                                ].join(" ")}
                              >
                                {label}
                              </span>
                            </div>
                            {i < steps.length - 1 && (
                              <div
                                className={[
                                  "flex-1 h-[2px] mx-2 mb-4 rounded-full transition-all duration-300",
                                  currentStep > stepNum ? "bg-cyan-400" : "bg-slate-700",
                                ].join(" ")}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Checkout overlay */}
            <AnimatePresence>
              {showCheckout && (
                <CheckoutScreen
                  agents={checkoutAgents}
                  messages={messages}
                  onClose={() => setShowCheckout(false)}
                  onGoHome={() => {
                    setMessages([]);
                    setInput("");
                    setSessionId(undefined);
                    setShowBooking(false);
                    setCtaUnlocked(false);
                    setChatViewOpen(false);
                    setShowCheckout(false);
                    setCheckoutAgents([]);
                    setMatchedAgents([]);
                    setPanelOpen(false);
                  }}
                />
              )}
            </AnimatePresence>

            {/* Main content: two-column on desktop when panel is open, single on mobile */}
            <div className="flex-1 flex overflow-hidden min-h-0 flex-col sm:flex-row">
              {/* Chat column — layout-animated so it glides when the side panel opens/closes */}
              <motion.div layout className="flex-1 flex flex-col overflow-hidden min-w-0 min-h-0" transition={{ type: "tween", duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}>
                {/* Scrollable message history */}
                <div ref={messagesRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
                  <div className="max-w-3xl mx-auto flex flex-col gap-2">

                    {/* Bluna intro — styled identically to agent chat bubble */}
                    {(() => {
                      const introText = "I'm Bluna, your new automation partner. Which agent are we building today?";
                      return (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className="flex gap-4 p-6 rounded-2xl w-full max-w-3xl mx-auto my-2 bg-purple-950/30 border border-cyan-400/15 backdrop-blur-sm"
                        >
                          <div className="w-8 h-8 rounded-full shrink-0 mt-0.5 shadow-sm overflow-hidden">
                            <img src={`${import.meta.env.BASE_URL}bluna.png`} alt="Bluna" className="w-full h-full object-cover object-top" />
                          </div>
                          <div className="flex-1 space-y-2 overflow-hidden min-w-0">
                            <span className="font-semibold text-sm text-cyan-300">Bluna</span>
                            <p className="text-sm leading-relaxed text-slate-200">{introText}</p>
                          </div>
                        </motion.div>
                      );
                    })()}

                    {messages.map((msg) => (
                      <ChatMessage
                        key={msg.id}
                        role={msg.role}
                        content={msg.content}
                        agentLookup={Object.fromEntries(
                          catalogueAgents.map((a) => [a.name, { imageUrl: a.imageUrl ?? null, role: a.tagline, description: a.description }])
                        )}
                        onSuggestionClick={(text) => {
                          if (!isPending) {
                            handleSubmit(undefined, text);
                          }
                        }}
                        onProceed={handleProceed}
                        onBrowseCatalogue={() => setShowCatalogue(true)}
                        onBookCall={() => setShowBooking(true)}
                        suppressAgentCards={true}
                      />
                    ))}

                    {isPending && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex gap-4 p-6 rounded-2xl w-full max-w-3xl mx-auto my-2 bg-purple-950/20 border border-cyan-400/10 backdrop-blur-sm"
                      >
                        <div className="w-8 h-8 rounded-full shrink-0 mt-0.5 shadow-sm overflow-hidden">
                          <img src={`${import.meta.env.BASE_URL}bluna.png`} alt="Bluna" className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 flex items-center h-8">
                          <div className="flex space-x-1.5">
                            <div className="w-2 h-2 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: "0ms" }} />
                            <div className="w-2 h-2 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: "150ms" }} />
                            <div className="w-2 h-2 rounded-full bg-cyan-400/70 animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <div ref={bottomRef} />
                  </div>
                </div>

                {/* Pinned input bar at bottom of chat column */}
                <div className="shrink-0 border-t border-cyan-400/15 bg-black/70 backdrop-blur-md px-4 sm:px-6 py-3">
                  <div className="max-w-3xl mx-auto">{inputBar}</div>
                </div>
              </motion.div>

              {/* Agent Match Panel — desktop side panel (slides in from right) */}
              <AnimatePresence>
                {panelOpen && matchedAgents.length > 0 && (
                  <motion.div
                    key="agent-panel-desktop"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: "22rem", opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ type: "tween", duration: 0.32, ease: [0.25, 0.46, 0.45, 0.94] }}
                    className="hidden sm:flex shrink-0 flex-col overflow-hidden"
                    style={{ minWidth: 0 }}
                  >
                    <AgentMatchPanel
                      matches={matchedAgents}
                      selectedIndexes={selectedAgentIndexes}
                      onToggleIndex={handleToggleAgentIndex}
                      agentLookup={Object.fromEntries(
                        catalogueAgents.map((a) => [a.name, { imageUrl: a.imageUrl ?? null, role: a.tagline, description: a.description }])
                      )}
                      onProceed={handleProceed}
                      onBrowseCatalogue={() => setShowCatalogue(true)}
                      onClose={() => setPanelOpen(false)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Agent Match Panel — mobile bottom sheet (stacks above input) */}
            <AnimatePresence>
              {panelOpen && matchedAgents.length > 0 && (
                <motion.div
                  key="agent-panel-mobile"
                  initial={{ height: 0 }}
                  animate={{ height: mobilePanelExpanded ? "15rem" : "3.5rem" }}
                  exit={{ height: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="sm:hidden shrink-0 overflow-hidden border-t border-border/60"
                >
                  <AgentMatchPanel
                    matches={matchedAgents}
                    selectedIndexes={selectedAgentIndexes}
                    onToggleIndex={handleToggleAgentIndex}
                    agentLookup={Object.fromEntries(
                      catalogueAgents.map((a) => [a.name, { imageUrl: a.imageUrl ?? null, role: a.tagline, description: a.description }])
                    )}
                    onProceed={handleProceed}
                    onBrowseCatalogue={() => setShowCatalogue(true)}
                    onClose={() => setPanelOpen(false)}
                    isCollapsible={true}
                    isExpanded={mobilePanelExpanded}
                    onToggleExpand={() => setMobilePanelExpanded((v) => !v)}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      <StoryModal open={showStory} onClose={() => setShowStory(false)} onBookCall={() => setShowBooking(true)} />
      <PrivacyModal open={showPrivacy} onClose={() => setShowPrivacy(false)} />
      <TermsModal open={showTerms} onClose={() => setShowTerms(false)} />

      {/* FAQ Modal */}
      <FAQModal open={showFAQ} onClose={() => setShowFAQ(false)} />

      {/* Agent Catalogue Modal */}
      <CatalogueModal
        isOpen={showCatalogue}
        onClose={() => setShowCatalogue(false)}
        onBuildAgent={handleBuildAgent}
        agents={catalogueAgents}
        loaded={catalogueLoaded}
      />

      {/* Agents Modal */}
      <AgentsModal
        open={showAgents}
        onClose={() => setShowAgents(false)}
        onSelect={(agent) => {
          setShowAgents(false);
          handleSubmit(undefined, `I need to build a ${agent}`);
        }}
        onStartChat={() => {
          setShowAgents(false);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
      />

      {/* Booking Modal */}
      <AnimatePresence>
        {showBooking && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/65 backdrop-blur-md p-4"
            onClick={() => setShowBooking(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              transition={{ type: "spring", stiffness: 300, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
              className="scifi-wave-bg rounded-2xl w-full max-w-2xl overflow-hidden flex flex-col border border-cyan-400/25"
              style={{
                maxHeight: "90vh",
                boxShadow: "0 24px 80px rgba(0,0,0,0.55), 0 0 40px rgba(0,229,255,0.15)",
              }}
            >
              {/* Modal header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-cyan-400/15 shrink-0 bg-black/50 backdrop-blur-sm">
                <div>
                  <p className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">Next step</p>
                  <p className="font-semibold text-[15px] text-white mt-0.5">One click to kick off your build. Let's get your agent online</p>
                </div>
                <button
                  onClick={() => setShowBooking(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-cyan-400/10 transition-colors text-slate-400 hover:text-cyan-300 text-xl leading-none"
                >
                  ×
                </button>
              </div>
              {/* Embedded calendar */}
              <iframe
                src="https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0ZTcPHbRXGRAW8FsR7JFUn_1bX_2d2rvzvtiJDafpUDp--olZ49vR5gZsz0PlaBs5nQYctqCcU?gv=true"
                style={{ border: 0, width: "100%", height: "600px", background: "white" }}
                frameBorder={0}
                title="Book a strategy call"
              />
              {/* Modal footer */}
              <div className="shrink-0 border-t border-cyan-400/15 px-5 py-4 bg-black/60 backdrop-blur-sm flex items-center justify-center gap-2">
                <CalendarCheck className="w-4 h-4 text-cyan-300 shrink-0" />
                <p className="text-[13px] font-semibold text-cyan-200">We are one step away from having your agent built</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Footer - only in empty state */}
      {!hasMessages && (
        <footer
          className="shrink-0 px-6 sm:px-8 py-3"
          style={{ borderTop: "1px solid rgba(192,38,211,0.18)", background: "rgba(8,6,18,0.55)" }}
        >
          <div className="flex items-center justify-between w-full">
            <a
              href="https://bluepeople.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity duration-200"
            >
              <span
                className="text-[11px]"
                style={{ fontFamily: "'Spline Sans', sans-serif", color: "#94a3b8" }}
              >
                powered by
              </span>
              <img src="/bluepeople-logo.png" alt="bluepeople" width="14" height="14" className="object-contain opacity-90" />
              <span
                className="text-[11px] font-medium"
                style={{ fontFamily: "'Spline Sans', sans-serif", color: "#22d3ee" }}
              >
                bluepeople.com
              </span>
            </a>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowPrivacy(true)}
                className="text-[11px] text-slate-400 hover:text-cyan-300 transition-colors"
                style={{ fontFamily: "'Spline Sans', sans-serif" }}
              >
                Privacy
              </button>
              <button
                onClick={() => setShowTerms(true)}
                className="text-[11px] text-slate-400 hover:text-cyan-300 transition-colors"
                style={{ fontFamily: "'Spline Sans', sans-serif" }}
              >
                Terms
              </button>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}
